import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';
import { WarehouseTable } from '../../../shared/table';
import { createColumnFactory } from '../../../shared/table/utils/tableUtils';
import type { ColumnDefinition, ServerFetchParams, TableDataRow } from '../../../shared/table/types/table';
import { adminDataInspectionService } from '../../../../services/adminDataInspectionService';
import { InspectionTableConfig, LinkToConfig } from '../../../../types/adminDataInspection';
import { StatusBadge } from './StatusBadge';

interface TableInspectorViewProps {
  tableConfig: InspectionTableConfig;
  onNavigateToTable?: (targetTable: string, filterKey: string, filterValue: string) => void;
  initialFilter?: { key: string; value: string };
}

type InspectionRow = TableDataRow & Record<string, any>;

export const TableInspectorView: React.FC<TableInspectorViewProps> = ({ tableConfig, onNavigateToTable, initialFilter }) => {
  const [data, setData] = useState<InspectionRow[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isLoadingRef = useRef(false);
  const latestParamsRef = useRef({ pageNumber: 1, pageSize: 50 });
  const filterDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const columnFactory = createColumnFactory<InspectionRow>();

  // Format date string for display
  const formatDate = (value: any): string => {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return String(value);
    return d.toLocaleString('nb-NO', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit'
    });
  };

  // Create a link renderer for navigable columns
  // Uses <a> with href so Ctrl+click natively opens in new tab (auth is now sync, no redirect)
  const createLinkRenderer = (linkTo: LinkToConfig) => (value: any) => {
    if (value == null || value === '' || value === 0) return <span className="text-gray-400">-</span>;
    if (!onNavigateToTable) return <span>{String(value)}</span>;
    const href = `/?module=admin-data-inspector&table=${linkTo.targetTable}&filterKey=${linkTo.filterKey}&filterValue=${encodeURIComponent(String(value))}`;
    return (
      <a
        href={href}
        onClick={(e) => {
          if (!e.ctrlKey && !e.metaKey) {
            // Normal click: SPA navigation (prevent full page reload)
            e.preventDefault();
            e.stopPropagation();
            onNavigateToTable(linkTo.targetTable, linkTo.filterKey, String(value));
          }
          // Ctrl+click: browser handles natively — opens href in new tab
        }}
        className="text-indigo-600 hover:text-indigo-800 hover:underline font-medium"
        title={`Vis i ${linkTo.targetTable}`}
      >
        {String(value)}
      </a>
    );
  };

  // Build WarehouseTable columns from table config
  const columns: ColumnDefinition<InspectionRow>[] = useMemo(() => {
    return tableConfig.columns.map(col => {
      // Status columns with badge renderer
      if (col.statusConfig) {
        const statusType = col.statusConfig.statusType;
        const customLabels = col.statusConfig.customLabels;
        return {
          ...columnFactory.createNumberColumn(col.key, col.key, col.label, {
            width: col.width || 120,
            sortable: col.sortable !== false,
            filterType: col.filterType as any,
          }),
          renderer: (value: any) => {
            if (value == null) return <span className="text-gray-400">-</span>;
            return <StatusBadge value={Number(value)} statusType={statusType} customLabels={customLabels} />;
          }
        };
      }

      // Date columns
      if (col.type === 'date') {
        return {
          ...columnFactory.createDateColumn(col.key, col.key, col.label, {
            width: col.width || 140,
            sortable: col.sortable !== false,
            filterType: (col.filterType || 'date') as any,
          }),
          formatter: formatDate,
        };
      }

      // Number columns: ID/reference fields (linkTo or filterType=text) use text column to avoid thousand separators
      if (col.type === 'number') {
        const isIdField = !!(col.linkTo || col.filterType === 'text');
        if (isIdField) {
          const colDef = columnFactory.createTextColumn(col.key, col.key, col.label, {
            width: col.width || 80,
            sortable: col.sortable !== false,
            filterType: col.filterType as any,
            alignment: 'right',
          });
          if (col.linkTo) {
            return { ...colDef, renderer: createLinkRenderer(col.linkTo) };
          }
          return colDef;
        }
        const colDef = columnFactory.createNumberColumn(col.key, col.key, col.label, {
          width: col.width || 80,
          sortable: col.sortable !== false,
          filterType: col.filterType as any,
        });
        return colDef;
      }

      // Default: text column with optional link
      const colDef = columnFactory.createTextColumn(col.key, col.key, col.label, {
        width: col.width || 120,
        sortable: col.sortable !== false,
        filterType: col.filterType as any,
      });
      if (col.linkTo) {
        return { ...colDef, renderer: createLinkRenderer(col.linkTo) };
      }
      return colDef;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableConfig, columnFactory, onNavigateToTable]);

  // Fetch data from server
  const fetchData = useCallback(async (params: { pageNumber: number; pageSize: number; sortColumn?: string; sortDirection?: string; filters?: Record<string, string> }) => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      const result = await adminDataInspectionService.getPagedTable<Record<string, any>>(
        tableConfig.endpoint,
        params
      );

      // Ensure each row has an id field (required by WarehouseTable)
      const rows: InspectionRow[] = result.items.map((item, index) => ({
        ...item,
        id: item.id ?? item.pListID ?? item.batchID ?? index,
      }));

      setData(rows);
      setTotalRecords(result.totalRecords);
      latestParamsRef.current = params;
    } catch (err) {
      console.error(`Error loading ${tableConfig.tableName}:`, err);
      setError(`Kunne ikke laste data. Prøv igjen.`);
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
    }
  }, [tableConfig.endpoint, tableConfig.tableName]);

  // Server-side pagination callback
  const handleServerFetch = useCallback((params: ServerFetchParams) => {
    const queryParams = {
      pageNumber: params.pageNumber,
      pageSize: params.pageSize,
      sortColumn: params.sortColumn,
      sortDirection: params.sortDirection,
      filters: params.filters,
    };

    // Debounce filter changes
    if (filterDebounceRef.current) {
      clearTimeout(filterDebounceRef.current);
    }

    const prevFilters = (latestParamsRef.current as any).filters;
    const isFilterChange = params.pageNumber === 1 && JSON.stringify(params.filters) !== JSON.stringify(prevFilters);

    if (isFilterChange) {
      filterDebounceRef.current = setTimeout(() => {
        fetchData(queryParams);
      }, 300);
    } else {
      fetchData(queryParams);
    }
  }, [fetchData]);

  const refreshData = useCallback(() => {
    fetchData(latestParamsRef.current as any);
  }, [fetchData]);

  // Initial load (with optional pre-applied filter from cross-table navigation)
  useEffect(() => {
    const params: any = {
      pageNumber: 1,
      pageSize: 50,
      sortColumn: tableConfig.defaultSort,
      sortDirection: 'desc'
    };
    if (initialFilter) {
      params.filters = { [initialFilter.key]: initialFilter.value };
    }
    fetchData(params);
  }, [fetchData, tableConfig.defaultSort, initialFilter]);

  // Cleanup debounce
  useEffect(() => {
    return () => {
      if (filterDebounceRef.current) {
        clearTimeout(filterDebounceRef.current);
      }
    };
  }, []);

  const tableActions = useMemo(() => [
    {
      id: 'refresh',
      label: 'Oppdater',
      icon: RefreshCw,
      onClick: () => refreshData(),
      variant: 'secondary' as const
    }
  ], [refreshData]);

  return (
    <div className="flex-1 min-h-0">
      <WarehouseTable
        data={data}
        columns={columns}
        config={{
          showPagination: true,
          showColumnConfig: true,
          showGlobalSearch: false,
          showFilters: true,
          showExport: false,
          showSummary: false,
          defaultPageSize: 50,
          allowColumnReorder: true,
          allowColumnResize: true,
          stickyHeader: true,
          compactMode: true,
          persistenceKey: `admin-inspect-${tableConfig.tableName}`
        }}
        serverPagination={{
          enabled: true,
          totalRecords: totalRecords,
          onFetchData: handleServerFetch
        }}
        tableActions={tableActions}
        loading={isLoading}
        error={error || undefined}
      />
    </div>
  );
};
